// ── <CopyLinksPanel> — the owner's COPY links for one forecast ──
//
// Pure props, from three hooks in AppInner: `links` / `liveCount` / `loading` /
// `revokingId` / `error` / `availability` / `onRevoke` from `useCopyLinks`;
// `expanded` / `onToggleExpanded` / `onCreate` from `useCopyLinksGate`; and
// `currentLink` / `copyableCopyId` from `lib/copyLinks/currentLink.js`; and
// `onCopyLink` / `justCopied` / `copyFailed` from `useMintedLinkStash`. No
// store reads, no fetch, no hook of its own, and — see the URL note below — it
// receives but never renders the one value that must stay out of the DOM (components
// stay pure-props — chatboard/CLAUDE.md "Frontend conventions").
//
// THE ACTION ROW LIVES HERE, not in AppInner. The "CREATE A COPY LINK" button was
// in App.jsx until the collapse landed; it moved because the collapse control has
// to share its row, and a two-element row split across two files is a layout that
// drifts. `data-copy-links-send` came with it unchanged — the e2e specs select on
// it. AppInner is now three wiring lines lighter, which is the direction that file
// is supposed to move in.
//
// FIVE THINGS ABOUT THIS FEATURE DECIDE WHAT THIS FILE MAY RENDER. Each fails
// silently — the panel still looks fine while telling the owner something untrue.
// Long form: docs/capsules/forecast-copy-links.md.
//
// 1. `state` IS THE SERVER'S ANSWER AND IS NEVER RECOMPUTED HERE. `listCopyLinks`
//    derives it through the module-private `isLive` (server/handlers/copyLinks.js:68,
//    used at :1302), which fails CLOSED on an unparseable `expires_at`; the handler
//    then passes `expires_at` through unnormalised (:1295-1300, because
//    `toISOString()` throws RangeError on a non-finite date), so a non-finite
//    expiry reaches the browser as `null`. A client-side recomputation would call
//    such a row live for a link redeem refuses. So every state question goes
//    through `linkStateLabel`, and `expiresAt` is display material only.
//
// 2. THE THREE STATES ARE A PRIORITY ORDER, not independent facts:
//    `revoked_at ? "revoked" : isLive(r) ? "live" : "expired"` (:1302). A link that
//    is both revoked and past expiry reports "revoked". So every decision below
//    tests `state === "live"` POSITIVELY rather than ruling the other two out, and
//    a non-live row never gets a countdown — for a revoked link the expiry is not
//    the operative fact, and showing one implies it is still redeemable.
//
// 3. AN UNRECOGNISED STATE MUST FAIL CLOSED. `linkStateLabel` answers "Unknown"
//    rather than defaulting to "Active" (lib/copyLinks/state.js:77-79), so it is
//    rendered as "Unknown", in the warning colour, with no Revoke button — telling
//    an owner a capability is live when nothing said so is the dangerous
//    direction, and papering over it here would undo that choice.
//
// 4. REVOCATION IS NEVER RETROSPECTIVE — it stops NEW redemptions, and copies
//    already made stay with whoever made them, forever. THIS PANEL NO LONGER
//    EXPLAINS THAT, by the product owner's decision (2026-08-06): a two-sentence
//    caveat sat above the Revoke buttons until then and was cut as too heavy for a
//    drawer read mostly at a glance. `SendCopyModal`'s NOT_RECALLABLE_WARNING still
//    states it, in front of the mint control — the moment the irreversible thing is
//    chosen — so the property is still taught where it can change a decision, and
//    merely not restated where it cannot. Don't reinstate it here without asking.
//
//    What survives here is the WORDING rule, an invariant rather than a teaching
//    aid: no string may say "delete", "recall", "withdraw", "unshare" — or "share"
//    at all, which is the other verb (A2). And nothing is optimistically flipped to
//    "Revoked": a falsy Bigtable write means the capability may still redeem
//    (copyLinks.js:1177-1183), so the list stays whatever the server last said and
//    only a refresh moves a row.
//
// 5. `revokingId` DISABLES ONE ROW, not the list — that is why the hook exposes an
//    id rather than a boolean. Two links on one forecast are two separate
//    capabilities and revoking one must not block revoking the other.
//
// NO SECRET IS RENDERED, AND NONE CAN BE. `GET /api/copy-links` returns no secret
// and no hash (:1298-1304) — mint returns the capability URL exactly once and the
// server stored only a hash — so this panel deliberately builds no `/copy/:id`
// URL either: one would look like the link while being unredeemable.
//
// …AND IT STILL HOLDS, THOUGH THE REASON CHANGED TWICE. `GET /api/copy-links` now
// returns each row's own URL to its owner, and this component RECEIVES one
// (`currentLink.url`) so it can hand it to the clipboard. It renders it NOWHERE.
//
// That is a stronger claim than the one it replaced, and worth stating precisely.
// The original property was "the panel is never given the URL" — true, but a fact
// about its props, which any future prop could quietly undo. For one commit
// (366975e51) the URL was displayed in full, by decision; the product owner then
// reversed it — a link is represented by its copy id, the same vocabulary the rows
// use. So the property is now "receives, never renders", which is about what this
// file DOES, and the test asserts it against a fixture whose rows all carry real
// URLs. Its value: no screenshot, bug bundle or snapshot of this drawer can contain
// a live capability.
//
// ONE COST, unsolved and deliberately so. The visible URL was also the manual
// fallback when the clipboard write fails — and that failure is reachable, not
// theoretical (an insecure origin has no `navigator.clipboard` at all). With the URL
// gone the owner has no route to the link on that path, so the failure message
// promises nothing: it reports the refusal and stops. Escalated rather than papered
// over with a half-fallback.
//
// A4 (the recipient's identity is never shown to the owner) needs no enforcement
// here beyond not inventing it: the list endpoint reports no redemption
// information at all, so there is nothing to leak. The words "recipient",
// "redeemed by" and "who has" are avoided so the assertion guarding A4 can stay a
// cheap text check.

import { C } from "../lib/colors.js";
import { expiryText, linkStateLabel } from "../lib/copyLinks/state.js";
import { fmtDateTime, fmtRelative } from "../lib/formatDates.js";
// A constant, not a hook — this component still calls no hook of its own. The
// hook owns the availability vocabulary, so the notice for a 503 is imported
// rather than re-typed.
import { SERVICE_UNAVAILABLE_MESSAGE } from "../hooks/useCopyLinks.js";
import { surface, ui } from "../lib/telemetry/instrument.js";
import { IconChevronRight } from "./ui/Icons.jsx";

/**
 * The two STICKY "not usable in this plane" conditions, which the hook reports on
 * `availability` rather than only on `error` (useCopyLinks.js) — they outlive the
 * error string, since a later successful read is the only thing that clears them.
 * Kept distinct because a 404 and a 503 are genuinely different situations.
 *
 * NEITHER STRING NAMES A CAUSE THE CLIENT CANNOT ACTUALLY DISTINGUISH, and the
 * second one used to. It read "…not configured in this deployment (COPY_LINKS_* is
 * unset)", but every handler 503s for either `!pool` or `!cfg`, so a deployment
 * with a DOWN DATABASE was told to go and check its env vars — a confident false
 * diagnosis, which is worse than a vague true one because it sends an operator to
 * the wrong file. It was also developer-facing copy (an env var name!) sitting in a
 * user-facing surface. The 404 line does name its cause, and may: `gateClosed` is
 * the ONLY 404 path on the list endpoint, so "switched off" is the whole truth.
 *
 * The real cause is not lost — see `availabilityDetail` below.
 *
 * A Map rather than an object literal, for the reason `STATE_LABELS` is one: an
 * object lookup answers for inherited keys.
 */
const AVAILABILITY_NOTICES = new Map([
  // Deliberately NOT the hook's `UNAVAILABLE_MESSAGE` ("not available in this
  // deployment"). Both are correct, and this one is the better sentence for the
  // person reading it: "switched off" says a decision was made, where "not
  // available" sounds like a fault. Kept as its own string on purpose.
  ["unavailable", "Copy links aren't turned on for your account."],
  // This one WAS a byte-identical copy of the hook's constant, which is how the
  // next copy edit lands in one file and not the other. Imported instead.
  ["dependencyUnavailable", SERVICE_UNAVAILABLE_MESSAGE],
]);

/**
 * Badge colour, keyed by the LABEL rather than the raw `state` — so it routes
 * through `linkStateLabel` and there is exactly one place that decides what a
 * state means. "Unknown" is deliberately a warning colour: a state we cannot
 * interpret should look wrong, not neutral.
 */
const STATE_COLORS = new Map([
  ["Active", C.green],
  ["Revoked", C.gray],
  ["Expired", C.gray],
  ["Unknown", C.orange],
]);

/** Matches `SendCopyModal`'s, so the URL reads identically in both places. */
const MONO = '"IBM Plex Mono",monospace';

const LABEL_STYLE = {
  fontSize: 12,
  color: C.grayL,
  fontWeight: 500,
};

/** Shared geometry for the three controls in the action row. */
const ACTION_BUTTON_STYLE = {
  borderRadius: 6,
  padding: "5px 10px",
  fontFamily: C.font,
  fontSize: 12,
  letterSpacing: 0,
  fontWeight: 500,
  cursor: "pointer",
  appearance: "none",
};

const NOTE_STYLE = {
  fontSize: 11,
  color: C.gray,
  lineHeight: 1.5,
  marginTop: 8,
};

const CHIP_STYLE = {
  fontSize: 11,
  color: C.grayL,
  letterSpacing: 0,
  padding: "1px 6px",
  border: `1px solid ${C.border}`,
  borderRadius: 4,
};

/**
 * The clipboard glyph — two offset sheets, the conventional "copy" mark.
 *
 * An inline SVG rather than a character (`⧉`, `⎘`): those have patchy coverage in
 * the mono stacks this app ships and degrade to a tofu box, which is a worse
 * affordance than no icon. `currentColor` makes it inherit the button's colour, so
 * it turns green with the confirmation for free. `aria-hidden` because the button
 * already carries the accessible name — an icon-only control needs one, and two
 * names would be read out twice.
 */
function CopyGlyph() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <rect x="5.5" y="5.5" width="9" height="9" rx="1.5" />
      <path d="M10.5 3.2V2.8a1.3 1.3 0 0 0-1.3-1.3H2.8a1.3 1.3 0 0 0-1.3 1.3v6.4a1.3 1.3 0 0 0 1.3 1.3h.4" />
    </svg>
  );
}

/**
 * One link row.
 *
 * Rendered as a local component rather than inline JSX only for readability; it
 * holds no state and reads nothing beyond its props.
 *
 * @param {object} props
 * @param {{copyId?: string, state?: string, expiresAt?: unknown, createdAt?: unknown,
 *   includeChat?: unknown}} props.link  one `GET /api/copy-links` row, verbatim
 * @param {boolean} props.busy   this row's revoke is in flight
 * @param {Function} props.onRevoke  called with the row's copyId
 * @param {boolean} props.copyable  a URL for THIS link is still held (see the panel)
 * @param {Function} [props.onCopy]  put that URL on the clipboard
 * @param {boolean} [props.justCopied]  the last copy SUCCEEDED
 * @param {boolean} [props.copyFailed]  the last copy FAILED — the write never
 *   landed, so the user must not be told it did
 * @param {number|undefined} props.now  epoch ms for the countdown; undefined lets
 *   `expiryText` and `fmtRelative` read the clock themselves
 */
function LinkRow({ link, busy, onRevoke, copyable, onCopy, justCopied, copyFailed, now }) {
  const label = linkStateLabel(link);
  // Positive test, per the priority order above.
  const live = link?.state === "live";
  // WHEN THIS LINK WAS MINTED. Relative, because the one thing it has to make
  // legible is the order the rows are already in — the server sorts `created_at
  // DESC` (copyLinks.js:1267) and both the hook and this component keep that order
  // VERBATIM. Nothing here re-sorts: a client-side sort would be a second copy of
  // the server's ordering rule, free to disagree with it silently.
  //
  // `fmtRelative` answers "" for a missing or unparseable value and the row then
  // renders nothing, which is the right fail-quiet: `created_at` is passed through
  // unnormalised for the same RangeError reason as `expiresAt` (copyLinks.js:1295-1301),
  // so `null` is reachable here. The absolute instant rides along as the `title`,
  // because "6d ago" is the wrong granularity for an audit question.
  const createdRelative = fmtRelative(link?.createdAt, now);
  return (
    <li
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "7px 0",
        borderTop: `1px solid ${C.border}`,
        fontFamily: C.font,
      }}
    >
      <span
        style={{
          fontSize: 12,
          fontWeight: "bold",
          color: STATE_COLORS.get(label) ?? C.orange,
          letterSpacing: 0,
          minWidth: 62,
        }}
      >
        {label}
      </span>
      {/* Only a LIVE row gets the expiry line. For every other state `expiryText`
          returns exactly the badge text (state.js:124-127), so rendering both
          would print "Revoked · Revoked"; and per §2 a countdown beside a revoked
          link reads as though the link were still redeemable. The test pins that
          equality, so a change to expiryText's non-live answers turns red here
          rather than silently dropping information. */}
      {live && <span style={{ fontSize: 11, color: C.grayL }}>{expiryText(link, now)}</span>}
      {createdRelative && (
        <span
          style={{ fontSize: 11, color: C.gray }}
          title={`Created ${fmtDateTime(link.createdAt)}`}
        >
          created {createdRelative}
        </span>
      )}
      {/* Material to the owner, and not recipient information: an opted-in link
          hands over a snapshot of the conversation as well as the forecast tree
          (capsule §6). Strict `=== true` — `includeChat` is a pg boolean, and a
          missing field must not read as an opt-in. */}
      {link?.includeChat === true && <span style={CHIP_STYLE}>includes chat</span>}
      {/* THE ID CELL, and the one place the clipboard is reachable.
          Click-to-copy-the-token, not a worded button: "copy" is overloaded on
          this surface (a copy OF A FORECAST vs a copy TO THE CLIPBOARD) and a
          second worded COPY control beside CREATE A COPY LINK reads as a rival
          spelling of the same verb. A glyph carries the clipboard sense; the
          word stays reserved for the forecast-copy sense.

          What is DISPLAYED is the id; what is COPIED is the whole URL, fragment
          included. That gap has to be unmistakable, so it is stated in the title
          and again in the confirmation, and the id is never presented as if it
          were the link. */}
      {copyable ? (
        <span style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
          {/* MUTUALLY EXCLUSIVE, and the failure branch is the one that matters:
              `navigator.clipboard` is secure-context only, so on an insecure origin
              (plain http, i.e. local dev) the write silently does nothing. Saying
              "copied" there is the expensive lie — the owner believes they captured
              a capability that cannot be shown again. The hook decides which of
              these is true by awaiting the write; this only renders the answer. */}
          {justCopied && (
            <span data-copy-link-copied="" style={{ fontSize: 11, color: C.green }}>
              full link copied
            </span>
          )}
          {copyFailed && (
            <span
              data-copy-link-copy-failed=""
              role="alert"
              style={{ fontSize: 11, color: C.red }}
              title="The browser refused the clipboard. This usually means an insecure connection or a denied permission."
            >
              couldn&rsquo;t copy
            </span>
          )}
          <button
            type="button"
            data-copy-link-copy-url={link.copyId}
            {...ui("copy-links.copy-url", link.copyId)}
            aria-label="Copy link to clipboard"
            onClick={onCopy}
            title="Copy the whole link to the clipboard, not just this id. Nothing can show it again."
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              color: justCopied ? C.green : C.action,
              whiteSpace: "nowrap",
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontFamily: C.font,
              appearance: "none",
            }}
          >
            {link.copyId}
            <CopyGlyph />
          </button>
        </span>
      ) : (
        <span
          style={{ fontSize: 11, color: C.gray, marginLeft: "auto", whiteSpace: "nowrap" }}
          title="Copy link id — the handle this link has in copy_links.* logs. Not the link itself."
        >
          {link?.copyId || "—"}
        </span>
      )}
      {live && (
        <button
          type="button"
          // Selectable by the e2e specs, which are where the click itself is
          // covered — a static-markup unit test cannot click (see the test file).
          data-copy-link-revoke={link.copyId}
          {...ui("copy-links.revoke", link.copyId)}
          disabled={busy}
          onClick={() => onRevoke?.(link.copyId)}
          title="Stop new copies being made from this link. Copies already made are unaffected."
          style={{
            cursor: busy ? "default" : "pointer",
            fontSize: 11,
            letterSpacing: 0,
            color: busy ? C.gray : C.actRed,
            border: `1px solid ${busy ? C.disabledBorder : C.actRed}`,
            borderRadius: 5,
            padding: "3px 9px",
            background: "transparent",
            appearance: "none",
            fontFamily: C.font,
            lineHeight: 1.2,
          }}
        >
          {busy ? "Revoking…" : "Revoke"}
        </button>
      )}
    </li>
  );
}

/**
 * The owner-side manage surface for one forecast's copy links.
 *
 * @param {object} props
 * @param {Array<object>} [props.links]  `GET /api/copy-links` rows, order and
 *   `state` verbatim from the server (never re-sorted, never re-derived)
 * @param {number} [props.liveCount]  the hook's count of live rows
 * @param {boolean} [props.loading]  a list read is in flight
 * @param {string|null} [props.revokingId]  the ONE row whose revoke is in flight
 * @param {string|null} [props.error]  the hook's already-user-facing message
 * @param {"unknown"|"available"|"unavailable"|"dependencyUnavailable"} [props.availability]
 * @param {string|null} [props.availabilityDetail]  the server's OWN wording for
 *   the condition, e.g. "Persistence disabled" vs "copy links are not configured".
 *   OPERATOR-FACING ONLY: rendered as a `data-*` attribute, never as text.
 * @param {Function} [props.onRevoke]  the hook's `revoke(copyId)`
 * @param {Function} [props.onCreate]  opens the mint modal (`useCopyLinksGate`'s
 *   `openSend`)
 * @param {string|null} [props.copyableCopyId]  the ONE link whose row may offer the
 *   clipboard — `currentLink`'s `copyId` (App.jsx), not the stash's.
 * @param {{copyId: string, url: string}|null} [props.currentLink]  THE link this
 *   panel is about — `lib/copyLinks/currentLink.js`'s `selectCurrentLink`, the
 *   newest live row with a usable URL. Null when there is none, and then the panel
 *   falls back to offering only "CREATE A COPY LINK".
 *
 *   **Its `url` is RECEIVED and never RENDERED.** That distinction is the whole
 *   property and it has moved twice: the URL was originally withheld from this
 *   component so it *could not* render one; then the product owner had it displayed
 *   in full; then had it replaced by the copy id, which is where it stands. So the
 *   guarantee is no longer structural — the string is right here in props — and is
 *   held instead by the render body plus the test that pins it against a fixture
 *   where every row carries a real URL. Anything added here that prints `url` puts
 *   a live capability into the DOM, and from there into screenshots and bug bundles.
 * @param {Function} [props.onCopyLink]  put that URL on the clipboard
 * @param {boolean} [props.justCopied]  the last copy SUCCEEDED
 * @param {boolean} [props.copyFailed]  the last copy FAILED
 * @param {boolean} [props.expanded]  show the rows. Default false; see
 *   `useCopyLinksGate` for why collapsed is the resting state.
 * @param {Function} [props.onToggleExpanded]
 * @param {number} [props.now]  epoch ms, injected so the countdown and the
 *   "created" line are testable (both read the clock when this is undefined)
 */
export default function CopyLinksPanel({
  links = [],
  liveCount = 0,
  loading = false,
  revokingId = null,
  error = null,
  availability = "unknown",
  availabilityDetail = null,
  onRevoke,
  onCreate,
  copyableCopyId = null,
  currentLink = null,
  onCopyLink,
  justCopied = false,
  copyFailed = false,
  expanded = false,
  onToggleExpanded,
  now,
  forecastActions = null,
}) {
  const notice = AVAILABILITY_NOTICES.get(availability) ?? null;
  const rows = Array.isArray(links) ? links : [];
  // WHICH ROW, IF ANY, MAY OFFER THE CLIPBOARD. Two independent conditions, and the
  // second is the self-healing one: the held URL must belong to a row the server
  // still calls LIVE. A link revoked in another tab, or one that simply expired,
  // comes back non-live on the next list read and the affordance disappears without
  // anything having to notice — offering a URL that 410s is worse than having lost
  // it. (`useMintedLinkStash.forgetIf` additionally *erases* the stored capability
  // on a revoke from this tab; this is the display half, that is the storage half,
  // and neither subsumes the other.)
  //
  // ONE TRANSIENT CONSEQUENCE, stated rather than engineered around. Because the
  // affordance is attached to a row, a held URL whose link has NO row yet — mint
  // succeeded but its follow-up list read failed — has nowhere to attach and is
  // briefly unreachable from this panel. It is not lost: `SendCopyModal` renders
  // the minted URL whether or not a later step failed (its booby-trap #1/#2), so
  // the owner is looking at it at that moment, and the glyph returns on the next
  // successful list read while the stash is still in date. A second fallback
  // surface for that window would be a second place to keep correct, for a state
  // that self-heals.
  //
  // A null `copyableCopyId` must never match a row whose own `copyId` is missing —
  // same trap as `revokingId`, same fix: the default is `null`, not `undefined`.
  const copyableRow =
    copyableCopyId && rows.some((l) => l?.copyId === copyableCopyId && l?.state === "live")
      ? copyableCopyId
      : null;
  // MORE THAN ONE, not one. A single link is fully represented by the block above —
  // its URL, its copy button, its revoke — so a toggle expanding to one row restates
  // what is already on screen. (And with none, it would claim content that does not
  // exist.) The clipping this collapse exists to prevent needs a list to clip.
  const showToggle = !notice && rows.length > 1;
  // Same one-row-not-the-list rule as `LinkRow`'s `busy`, applied to the current
  // link's own revoke button: two links are two capabilities.
  const currentBusy = currentLink !== null && revokingId === currentLink.copyId;
  // THE SAME CALL THE ROWS MAKE. It was `fmtDateTime` for one commit, and the echo
  // that produced is why it is not any more: the identity line and this link's own
  // row sat eight lines apart saying "created Aug 6, 2026, 1:00 AM" and "created 7h
  // ago" about the same link with the same verb, which reads as two events. One
  // function decides what "created" looks like on this surface.
  //
  // It doubles as the usability guard, which is the tidy part — `fmtRelative`
  // answers "" for a missing or unparseable timestamp (both reachable, since the
  // handler passes `created_at` through unnormalised), so there is no separate
  // check to keep in step with the renderer.
  const createdRelative = currentLink ? fmtRelative(currentLink.createdAt, now) : "";

  // Built here rather than inline because it has two homes (see below). `null` when
  // there is nothing to toggle, so both call sites are a plain truthiness check.
  const toggleButton = showToggle ? (
    <button
      type="button"
      data-copy-links-toggle=""
      {...ui("copy-links.toggle")}
      aria-expanded={expanded}
      onClick={onToggleExpanded}
      title={
        expanded ? "Hide the copy links for this forecast" : "Show the copy links for this forecast"
      }
      style={{
        ...ACTION_BUTTON_STYLE,
        ...LABEL_STYLE,
        marginLeft: "auto",
        background: "transparent",
        border: "none",
        padding: "4px 0",
      }}
    >
      {/* Count from the hook, not recounted here — one place decides what
                  "live" means. It rides on the toggle because it is the whole of what
                  a collapsed panel has to say. */}
      All copy links ({liveCount} active of {rows.length}){" "}
      <span
        aria-hidden="true"
        style={{
          display: "inline-flex",
          verticalAlign: "middle",
          transform: expanded ? "rotate(90deg)" : "none",
        }}
      >
        <IconChevronRight width={14} height={14} />
      </span>
    </button>
  ) : null;
  return (
    <div
      data-copy-links-panel=""
      data-telemetry-redact=""
      {...surface("copy-links")}
      style={{ fontFamily: C.font }}
    >
      {/* ONE ROW, THREE ZONES: identity, the actions flowing straight after it, and
          the list toggle alone at the far right.

          The buttons deliberately do NOT get `marginLeft: auto`. They belong to the
          link the text just named, so they read as its actions when they sit beside
          it and as unattached controls when flung to the opposite edge. Only
          `ALL COPY LINKS` is pushed right — it is about the whole list, not this
          link, and the right edge is where it already lived.

          WHAT WRAPS, AND IN WHAT ORDER. Four things plus a 36-char id does not fit
          every width, and the drawer narrows. The identity is `flex: 0 1 auto;
          min-width: 0` — it does not grow (that is what keeps the buttons beside it)
          but it yields first, wrapping the id inside its own box. Then the row wraps
          in source order, so the LAST child moves first: the toggle drops to its own
          line, then the button group, and only then do the buttons wrap among
          themselves — REVOKE, then CREATE NEW COPY LINK. COPY TO CLIPBOARD is the
          last thing standing. That ordering is the requirement, not a side effect: a
          primary action that vanishes at a narrow width is the worst outcome a
          layout change can have.

          `data-copy-links-send` stays on the mint control in BOTH shapes — it is the
          same action wearing a different label, and the e2e specs select on it. The
          copy and revoke controls carry `data-current-link-*` names rather than the
          row-level `data-copy-link-*` ones: several assertions COUNT occurrences of
          `data-copy-link-revoke`, and a name containing it as a substring would
          silently inflate them. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 2,
        }}
      >
        {/* THE IDENTITY LINE, AHEAD OF THE BUTTONS BECAUSE IT NAMES WHAT THEY ACT ON.
            Read first, then act — with three controls below it, "which link is this?"
            has to be answered before "revoke" means anything.

            A full selectable URL sat here for one commit (366975e51) and the product
            owner reversed it: a link is represented by its copy id, the same
            vocabulary the rows in ALL COPY LINKS use. So the no-URL-in-the-markup
            property is BACK, and stronger than the original — this component now
            legitimately RECEIVES `currentLink.url`, and renders it nowhere.

            "Forecast copy link" is the exact phrasing, and every word of it is load
            bearing (A2): "copy" carries the verb, and dropping it to "Forecast link"
            or shortening to "link" would both read as a share link, which is the
            mental model the invariant exists to prevent.

            THE ID IS NOT THE LINK, and the sentence must not invite pasting it into a
            browser. Two things say so without lengthening the copy: the id is styled
            as a mono token rather than as prose, so it reads as a reference; and the
            title spells it out. The URL is what redeems.

            The date is ABSOLUTE here where the rows are relative ("created 7h ago").
            Deliberate: this line is a record of one link, and "7h ago" is the wrong
            granularity for identifying it, while the list is a scannable ordering
            where relative is exactly right. The two use the SAME formatter that the
            rows' own hover titles use, so the absolute form never differs between the
            two places. `fmtRelative` is the usability guard — it answers "" for a
            missing or unparseable timestamp (both reachable: the handler passes
            `created_at` through unnormalised), and then the clause is simply omitted
            rather than printing "Invalid Date". */}
        {currentLink && (
          <div
            data-current-link-id={currentLink.copyId}
            title="Copy link id — the handle this link has in copy_links.* logs. Not the link itself; the URL is what redeems."
            style={{
              ...NOTE_STYLE,
              marginTop: 0,
              color: C.grayL,
              // `0 1 auto`: it does NOT grow, so the buttons sit immediately after
              // the text rather than being flung to the far edge — only the toggle
              // gets a right margin. It DOES shrink, and `minWidth: 0` is what
              // permits that: a flex item's default `min-width: auto` refuses to go
              // below its content, so without it the 36-char id would push the
              // buttons out of the row instead of wrapping inside its own box.
              flex: "0 1 auto",
              minWidth: 0,
            }}
          >
            Forecast copy link{" "}
            <span style={{ fontFamily: MONO, color: C.gray, wordBreak: "break-all" }}>
              {currentLink.copyId}
            </span>
            {createdRelative ? ` created ${createdRelative}` : ""}
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          {currentLink && (
            <button
              type="button"
              data-current-link-copy=""
              {...ui("copy-links.copy-current")}
              onClick={onCopyLink}
              title="Put this copy link on the clipboard"
              style={{
                ...ACTION_BUTTON_STYLE,
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: C.action,
                color: C.bg,
                border: "none",
              }}
            >
              Copy to clipboard
              <CopyGlyph />
            </button>
          )}
          <button
            type="button"
            data-copy-links-send=""
            {...ui("copy-links.create")}
            onClick={onCreate}
            title="Create a link that gives someone their own copy of this forecast"
            style={{
              ...ACTION_BUTTON_STYLE,
              background: currentLink ? "transparent" : C.btnBg,
              color: C.action,
              border: `1px solid ${C.action}`,
            }}
          >
            {currentLink ? "Create new copy link" : "Create a copy link"}
          </button>
          {currentLink && (
            <button
              type="button"
              data-current-link-revoke={currentLink.copyId}
              {...ui("copy-links.revoke", currentLink.copyId)}
              disabled={currentBusy}
              onClick={() => onRevoke?.(currentLink.copyId)}
              title="Stop new copies being made from this link. Copies already made are unaffected."
              style={{
                ...ACTION_BUTTON_STYLE,
                background: "transparent",
                color: currentBusy ? C.gray : C.actRed,
                border: `1px solid ${currentBusy ? C.disabledBorder : C.actRed}`,
              }}
            >
              {currentBusy ? "Revoking…" : "Revoke"}
            </button>
          )}
        </div>
        {/* ACTIONS ON THE FORECAST, not on the current link — today just SHARE.
            Deliberately a SIBLING of the button group above rather than a member of
            it: that group's own comment explains it reads as "the actions of the
            link the text just named", and SHARE is not one. It acts on the forecast
            the whole panel is about, so it sits in the same row but outside that
            grouping.

            Passed in rather than built here because its gate is INDEPENDENT of this
            panel's: copy links and org sharing have separate GOFF flags, so either
            can be on with the other off. This component must not become the thing
            that decides whether sharing is offered — the caller renders the button
            elsewhere when copy links are unavailable and this panel is absent.

            Wrap order: the row still wraps last-child-first, so the toggle drops
            away first, then this, then the link's own buttons — which keeps
            COPY TO CLIPBOARD standing longest, as the group comment requires. */}
        {forecastActions}
        {/* THE ONLY THING PUSHED RIGHT. Its own `marginLeft: auto` does it, so the
            toggle needs no wrapper and stays on this row in BOTH shapes — beside a
            lone CREATE A COPY LINK, or out past the three action buttons. It earns
            the far edge because it is not an action on the current link: it opens a
            list about all of them. */}
        {toggleButton}
      </div>

      {currentLink && justCopied && (
        <div data-current-link-copied="" style={{ ...NOTE_STYLE, color: C.green }}>
          Copied to clipboard.
        </div>
      )}
      {currentLink && copyFailed && (
        <div data-current-link-copy-failed="" role="alert" style={{ ...NOTE_STYLE, color: C.red }}>
          Couldn&rsquo;t copy — your browser refused the clipboard.
        </div>
      )}

      {notice ? (
        // The two sticky conditions replace the list entirely: `links` is empty on
        // either, and the hook's `error` says the same thing in different words, so
        // rendering both would say it twice.
        //
        // THE DIAGNOSIS RIDES ON A `data-*` ATTRIBUTE, not in the text. An operator
        // opens devtools and reads exactly which condition fired; a user reads a
        // sentence that claims nothing the client cannot actually know. Same split
        // `SendCopyModal` already uses for `data-error-kind`. Deliberately not a
        // `title`, which would surface it to the user as a tooltip.
        <div
          style={NOTE_STYLE}
          data-copy-links-notice={availability}
          data-server-detail={availabilityDetail || ""}
        >
          {notice}
        </div>
      ) : (
        <>
          {/* THREE THINGS STAY OUTSIDE THE COLLAPSE, for one reason: each explains
              why the list is absent, stale or wrong, and a toggle reading "COPY
              LINKS (0 active of 0)" over a hidden failure is the misreading this
              whole surface is built to avoid. `error` in particular is transient —
              it can arrive while the panel is collapsed. */}
          {error && (
            <div style={{ ...NOTE_STYLE, color: C.red }} role="alert">
              {error}
            </div>
          )}
          {loading && <div style={NOTE_STYLE}>Loading copy links…</div>}
          {rows.length === 0
            ? !loading && <div style={NOTE_STYLE}>No copy links for this forecast yet.</div>
            : expanded && (
                <ul style={{ listStyle: "none", margin: "8px 0 0", padding: 0 }}>
                  {rows.map((link, i) => (
                    <LinkRow
                      key={link?.copyId || `row-${i}`}
                      link={link}
                      // ONE row, never the list (§5). The `revokingId = null`
                      // default above is load-bearing rather than cosmetic: it is
                      // what stops an ABSENT prop from matching a row whose own
                      // `copyId` is missing, since `undefined === undefined` holds.
                      // An extra `revokingId != null &&` guard here was
                      // mutation-verified as unkillable — the default already
                      // subsumes it (same finding as the dropped `expiresAt !== ""`
                      // guard in lib/copyLinks/state.js).
                      busy={revokingId === link?.copyId}
                      onRevoke={onRevoke}
                      copyable={copyableRow !== null && copyableRow === link?.copyId}
                      onCopy={onCopyLink}
                      justCopied={justCopied}
                      copyFailed={copyFailed}
                      now={now}
                    />
                  ))}
                </ul>
              )}
        </>
      )}
    </div>
  );
}
